/**
 * Icon barrel — per-glyph imports so the bundle carries the icons we use,
 * not the whole library.
 *
 * Importing from `lucide-react-native` pulls its root barrel, and Metro has
 * no tree shaking: all 1,671 glyph modules landed in every build. Measured
 * on the web export, that barrel was 1,480 KB of a 9.75 MB entry chunk —
 * the single largest dependency after Sentry.
 *
 * The deep paths are lucide's real module layout (its `exports` map only
 * publishes `.` and `./icons`, both barrels). Every alias below is taken
 * verbatim from lucide's own `dist/esm/lucide-react-native.js`, so the
 * binding names are identical to what the barrel exported.
 *
 * Adding an icon: import it here from its glyph module, then import the
 * name from `@/components/ui/icons`. Never import `lucide-react-native`
 * directly — that reintroduces the whole library.
 */
import AlertCircle from 'lucide-react-native/dist/esm/icons/circle-alert.js'
import AlertTriangle from 'lucide-react-native/dist/esm/icons/triangle-alert.js'
import ArrowRight from 'lucide-react-native/dist/esm/icons/arrow-right.js'
import BadgeCheck from 'lucide-react-native/dist/esm/icons/badge-check.js'
import Bell from 'lucide-react-native/dist/esm/icons/bell.js'
import BellOff from 'lucide-react-native/dist/esm/icons/bell-off.js'
import BellRing from 'lucide-react-native/dist/esm/icons/bell-ring.js'
import Building2 from 'lucide-react-native/dist/esm/icons/building-2.js'
import Calendar from 'lucide-react-native/dist/esm/icons/calendar.js'
import CalendarCheck from 'lucide-react-native/dist/esm/icons/calendar-check.js'
import CalendarOff from 'lucide-react-native/dist/esm/icons/calendar-off.js'
import CalendarX from 'lucide-react-native/dist/esm/icons/calendar-x.js'
import Camera from 'lucide-react-native/dist/esm/icons/camera.js'
import Car from 'lucide-react-native/dist/esm/icons/car.js'
import CarFront from 'lucide-react-native/dist/esm/icons/car-front.js'
import Check from 'lucide-react-native/dist/esm/icons/check.js'
import CheckCheck from 'lucide-react-native/dist/esm/icons/check-check.js'
import CheckCircle from 'lucide-react-native/dist/esm/icons/circle-check-big.js'
import CheckCircle2 from 'lucide-react-native/dist/esm/icons/circle-check.js'
import ChevronDown from 'lucide-react-native/dist/esm/icons/chevron-down.js'
import ChevronLeft from 'lucide-react-native/dist/esm/icons/chevron-left.js'
import ChevronRight from 'lucide-react-native/dist/esm/icons/chevron-right.js'
import ChevronUp from 'lucide-react-native/dist/esm/icons/chevron-up.js'
import Circle from 'lucide-react-native/dist/esm/icons/circle.js'
import Clock from 'lucide-react-native/dist/esm/icons/clock.js'
import Clock3 from 'lucide-react-native/dist/esm/icons/clock-3.js'
import CreditCard from 'lucide-react-native/dist/esm/icons/credit-card.js'
import DollarSign from 'lucide-react-native/dist/esm/icons/dollar-sign.js'
import DoorOpen from 'lucide-react-native/dist/esm/icons/door-open.js'
import Download from 'lucide-react-native/dist/esm/icons/download.js'
import Droplets from 'lucide-react-native/dist/esm/icons/droplets.js'
import Edit3 from 'lucide-react-native/dist/esm/icons/pen-line.js'
import ExternalLink from 'lucide-react-native/dist/esm/icons/external-link.js'
import Eye from 'lucide-react-native/dist/esm/icons/eye.js'
import EyeOff from 'lucide-react-native/dist/esm/icons/eye-off.js'
import FileBadge from 'lucide-react-native/dist/esm/icons/file-badge.js'
import FileCheck from 'lucide-react-native/dist/esm/icons/file-check.js'
import FileText from 'lucide-react-native/dist/esm/icons/file-text.js'
import FileX from 'lucide-react-native/dist/esm/icons/file-x.js'
import Fingerprint from 'lucide-react-native/dist/esm/icons/fingerprint-pattern.js'
import Fuel from 'lucide-react-native/dist/esm/icons/fuel.js'
import Gauge from 'lucide-react-native/dist/esm/icons/gauge.js'
import Globe from 'lucide-react-native/dist/esm/icons/globe.js'
import HardHat from 'lucide-react-native/dist/esm/icons/hard-hat.js'
import Headphones from 'lucide-react-native/dist/esm/icons/headphones.js'
import Heart from 'lucide-react-native/dist/esm/icons/heart.js'
import Home from 'lucide-react-native/dist/esm/icons/house.js'
import IdCard from 'lucide-react-native/dist/esm/icons/id-card.js'
import Image from 'lucide-react-native/dist/esm/icons/image.js'
import ImageOff from 'lucide-react-native/dist/esm/icons/image-off.js'
import Info from 'lucide-react-native/dist/esm/icons/info.js'
import Landmark from 'lucide-react-native/dist/esm/icons/landmark.js'
import Lock from 'lucide-react-native/dist/esm/icons/lock.js'
import LockKeyhole from 'lucide-react-native/dist/esm/icons/lock-keyhole.js'
import LogOut from 'lucide-react-native/dist/esm/icons/log-out.js'
import Luggage from 'lucide-react-native/dist/esm/icons/luggage.js'
import Mail from 'lucide-react-native/dist/esm/icons/mail.js'
import Map from 'lucide-react-native/dist/esm/icons/map.js'
import MapPin from 'lucide-react-native/dist/esm/icons/map-pin.js'
import MessageCircle from 'lucide-react-native/dist/esm/icons/message-circle.js'
import Minus from 'lucide-react-native/dist/esm/icons/minus.js'
import Moon from 'lucide-react-native/dist/esm/icons/moon.js'
import MoreVertical from 'lucide-react-native/dist/esm/icons/ellipsis-vertical.js'
import Navigation from 'lucide-react-native/dist/esm/icons/navigation.js'
import PackageX from 'lucide-react-native/dist/esm/icons/package-x.js'
import Palette from 'lucide-react-native/dist/esm/icons/palette.js'
import Paperclip from 'lucide-react-native/dist/esm/icons/paperclip.js'
import Pencil from 'lucide-react-native/dist/esm/icons/pencil.js'
import Phone from 'lucide-react-native/dist/esm/icons/phone.js'
import PhoneCall from 'lucide-react-native/dist/esm/icons/phone-call.js'
import Plus from 'lucide-react-native/dist/esm/icons/plus.js'
import Receipt from 'lucide-react-native/dist/esm/icons/receipt.js'
import RefreshCw from 'lucide-react-native/dist/esm/icons/refresh-cw.js'
import RotateCcw from 'lucide-react-native/dist/esm/icons/rotate-ccw.js'
import Scan from 'lucide-react-native/dist/esm/icons/scan.js'
import ScanFace from 'lucide-react-native/dist/esm/icons/scan-face.js'
import Search from 'lucide-react-native/dist/esm/icons/search.js'
import SearchX from 'lucide-react-native/dist/esm/icons/search-x.js'
import Send from 'lucide-react-native/dist/esm/icons/send.js'
import Settings from 'lucide-react-native/dist/esm/icons/settings.js'
import Shield from 'lucide-react-native/dist/esm/icons/shield.js'
import ShieldAlert from 'lucide-react-native/dist/esm/icons/shield-alert.js'
import ShieldCheck from 'lucide-react-native/dist/esm/icons/shield-check.js'
import SkipForward from 'lucide-react-native/dist/esm/icons/skip-forward.js'
import Slash from 'lucide-react-native/dist/esm/icons/slash.js'
import SlidersHorizontal from 'lucide-react-native/dist/esm/icons/sliders-horizontal.js'
import Smartphone from 'lucide-react-native/dist/esm/icons/smartphone.js'
import Sparkles from 'lucide-react-native/dist/esm/icons/sparkles.js'
import Star from 'lucide-react-native/dist/esm/icons/star.js'
import Sun from 'lucide-react-native/dist/esm/icons/sun.js'
import Tag from 'lucide-react-native/dist/esm/icons/tag.js'
import Tractor from 'lucide-react-native/dist/esm/icons/tractor.js'
import Trash2 from 'lucide-react-native/dist/esm/icons/trash-2.js'
import TrendingUp from 'lucide-react-native/dist/esm/icons/trending-up.js'
import Truck from 'lucide-react-native/dist/esm/icons/truck.js'
import Upload from 'lucide-react-native/dist/esm/icons/upload.js'
import User from 'lucide-react-native/dist/esm/icons/user.js'
import UserCheck from 'lucide-react-native/dist/esm/icons/user-check.js'
import UserMinus from 'lucide-react-native/dist/esm/icons/user-minus.js'
import UserPen from 'lucide-react-native/dist/esm/icons/user-pen.js'
import UserPlus from 'lucide-react-native/dist/esm/icons/user-plus.js'
import UserRound from 'lucide-react-native/dist/esm/icons/user-round.js'
import Users from 'lucide-react-native/dist/esm/icons/users.js'
import Wallet from 'lucide-react-native/dist/esm/icons/wallet.js'
import WifiOff from 'lucide-react-native/dist/esm/icons/wifi-off.js'
import Wrench from 'lucide-react-native/dist/esm/icons/wrench.js'
import X from 'lucide-react-native/dist/esm/icons/x.js'
import XCircle from 'lucide-react-native/dist/esm/icons/circle-x.js'
import Zap from 'lucide-react-native/dist/esm/icons/zap.js'

export type { LucideIcon } from 'lucide-react-native'

export {
  AlertCircle,
  AlertCircle as CircleAlert,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Bell,
  BellOff,
  BellRing,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarOff,
  CalendarX,
  Camera,
  Car,
  CarFront,
  Check,
  CheckCheck,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  Clock,
  Clock3,
  CreditCard,
  DollarSign,
  DoorOpen,
  Download,
  Droplets,
  Edit3,
  ExternalLink,
  Eye,
  EyeOff,
  FileBadge,
  FileCheck,
  FileText,
  FileX,
  Fingerprint,
  Fuel,
  Gauge,
  Globe,
  HardHat,
  Headphones,
  Heart,
  Home,
  IdCard,
  Image,
  Image as ImageIcon,
  ImageOff,
  Info,
  Landmark,
  Lock,
  LockKeyhole,
  LogOut,
  Luggage,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Minus,
  Moon,
  MoreVertical,
  Navigation,
  PackageX,
  Palette,
  Paperclip,
  Pencil,
  Phone,
  PhoneCall,
  Plus,
  Receipt,
  RefreshCw,
  RotateCcw,
  Scan,
  ScanFace,
  Search,
  SearchX,
  Send,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SkipForward,
  Slash,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  Sun,
  Tag,
  Tractor,
  Trash2,
  TrendingUp,
  Truck,
  Upload,
  User,
  UserCheck,
  UserMinus,
  UserPen,
  UserPlus,
  UserRound,
  Users,
  Wallet,
  WifiOff,
  Wrench,
  X,
  XCircle,
  Zap,
}
